import React, { useCallback } from 'react';
import BaseTextField from 'common/components/base-text-field';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Box,
    Button,
    Fade,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { headerSearchInputSchema } from 'common/schema';
import FormProvider from 'common/components/form-provider';
import {
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'common/hook';
import {
    setCampaignsSearchForm,
    clearCampaignsSearchForm,
} from 'slices/campaignsSlice';
import { ReactComponent as TuningIcon } from 'assets/images/icons/tuning.svg';
import { ReactComponent as BellIcon } from 'assets/images/icons/bell.svg';
import MainHeaderCampaignFilterPopover from './main-header-campaign-filter-popover';
import CampaignForm from 'pages/admin/campaign/components/campaign-form';
import { styleInput } from 'common/constants';
import { common } from '@mui/material/colors';
import { createStyleSheet } from 'utils/helpers';
import { useLocation, useNavigate } from 'react-router-dom';
import BrandLogo from 'common/components/brand-logo';
import PaymentFilter from 'pages/admin/payment/components/payment-filter';
import AccountFilter from 'pages/admin/account-management/components/account-filter';
import CustomerFilter from 'pages/admin/customer/components/customer-filter';
import {
    setAccountSearchForm,
    clearAccountSearchForm,
} from 'slices/accountSlice';
import { setCustomerSearchForm } from 'slices/customerSlice';
import Notification from './notification';
import OverviewFilter from 'pages/admin/overview/components/overview-filter';
import { clearCustomerSearchForm } from 'slices/customerSlice';
import {
    clearOverviewSearchForm,
    setOverviewSearchForm,
} from 'slices/overviewSlice';
import DataCollectFilter from 'pages/admin/campaign-data-collect/components/data-collect-filter';
import {
    setDataCollectSearchForm,
    clearDataCollectSearchForm,
} from 'slices/dataCollectSlice';
import { setPaymentSearchForm } from 'slices/paymentSlice';
import CampaignOverviewFilter from 'pages/admin/campaign-overview/components/campaign-overview-filter';

interface IMainHeaderProps {
    showSidebar: boolean;
}
const MainHeader: React.FC<IMainHeaderProps> = ({ showSidebar }) => {
    const location = useLocation();
    const { t } = useTranslation();
    const [addCampaignDialogOpen, setAddCampaignDialogOpen] =
        React.useState<boolean>(false);
    const navigate = useNavigate();
    const userInfo = useAppSelector((state) => state.user);
    const dispatch = useAppDispatch();
    const methods = useForm({
        resolver: yupResolver(headerSearchInputSchema),
        defaultValues: {
            searchKeyWord: '',
        },
    });

    const { handleSubmit, reset } = methods;
    const onSubmit = handleSubmit(async (data) => {
        const params = { ...data };
        switch (location.pathname) {
            case '/admin/campaign':
                dispatch(setCampaignsSearchForm(params));
                break;
            case '/admin/payment':
                dispatch(setPaymentSearchForm(params));
                break;
            case '/admin/account-management':
                dispatch(setAccountSearchForm(params));
                break;
            case '/admin/customer':
                dispatch(setCustomerSearchForm(params));
                break;
            case '/admin/overview':
                dispatch(setOverviewSearchForm(params));
                break;
            default:
                if (
                    location.pathname.includes('/admin/campaign/data-collect')
                ) {
                    dispatch(setDataCollectSearchForm(params));
                }
                break;
        }
    });

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const [filterState, setFilterState] = React.useState<
        Record<string, { anchorEl: HTMLButtonElement | null }>
    >({});

    const handleFilterClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        id: string
    ) => {
        setFilterState((prev) => ({
            ...prev,
            [id]: { anchorEl: event.currentTarget },
        }));
    };

    const handleFilterClose = (id: string) => {
        setFilterState((prev) => ({
            ...prev,
            [id]: { anchorEl: null },
        }));
    };

    React.useEffect(() => {
        dispatch(clearCampaignsSearchForm());
        dispatch(clearAccountSearchForm());
        dispatch(clearCustomerSearchForm());
        dispatch(clearOverviewSearchForm());
        dispatch(clearDataCollectSearchForm());
        reset();
    }, [location.pathname, dispatch, reset]);

    const handleGetPathName = useCallback(() => {
        switch (location.pathname) {
            case '/admin/campaign':
                return 'campaignFilter';
            case '/admin/payment':
                return 'paymentFilter';
            case '/admin/account-management':
                return 'accountFilter';
            case '/admin/customer':
                return 'customerFilter';
            case '/admin/overview':
                return 'overviewFilter';
            default:
                if (
                    location.pathname.includes('/admin/campaign/data-collect')
                ) {
                    return 'dataCollectFilter';
                }
                if (location.pathname.includes('/admin/campaign/overview')) {
                    return 'campaignOverviewFilter';
                }
                return '';
        }
    }, [location.pathname]);

    const [anchorElNotification, setaAnchorElNotification] =
        React.useState<HTMLButtonElement | null>(null);

    const handleOpenNotification = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        setaAnchorElNotification(event.currentTarget);
    };

    const handleCloseNotification = () => {
        setaAnchorElNotification(null);
    };

    const openPopover = Boolean(anchorElNotification);
    const idPopover = openPopover ? 'popover-notification' : undefined;
    const isFilterOpen = (id: string) => Boolean(filterState[id]?.anchorEl);
    const getFilterId = (id: string) => (isFilterOpen(id) ? id : undefined);
    return (
        <Box width={'100%'}>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '26px',
                    }}
                >
                    {!showSidebar && (
                        <BrandLogo
                            style={{
                                width: '127px',
                                height: '91.64px',
                                marginRight: '131px',
                            }}
                            onClick={() => navigate('/')}
                        />
                    )}
                    <BaseTextField
                        autoComplete="off"
                        name="searchKeyWord"
                        size="medium"
                        fullWidth={false}
                        placeholder={t('placeholder.search.here')}
                        sx={styles.search}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconButton
                                        type="submit"
                                        aria-label="search"
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {userInfo.attributes.role !== 'accountant' && (
                        <Button
                            onClick={() => setAddCampaignDialogOpen(true)}
                            variant="contained"
                            sx={styles.btnAddCampaign}
                        >
                            {t('layout.header.button.addCampaign')}
                        </Button>
                    )}

                    <Button
                        aria-describedby={idPopover}
                        onClick={(event) => handleOpenNotification(event)}
                        variant="contained"
                        sx={{
                            minWidth: '70px',
                            height: '70px',
                            borderRadius: '50%',
                            boxShadow: 'none',
                            border: 'none',
                            backgroundColor: common.white,
                            '&:hover': {
                                backgroundColor: common.white,
                            },
                            '& .MuiButton-icon': {
                                margin: 0,
                            },
                        }}
                        startIcon={<BellIcon />}
                    />
                    <Button
                        aria-controls={getFilterId(handleGetPathName())}
                        variant="contained"
                        sx={{
                            minWidth: '70px',
                            height: '70px',
                            borderRadius: '50%',
                            backgroundColor: common.white,
                            boxShadow: 'none',
                            border: 'none',
                            '&:hover': {
                                backgroundColor: common.white,
                            },
                            '& .MuiButton-icon': {
                                margin: 0,
                                width: '26px',
                                height: '32px',
                            },
                        }}
                        startIcon={<TuningIcon />}
                        onClick={(event) =>
                            handleFilterClick(event, handleGetPathName())
                        }
                    />
                    <Box>
                        <Button
                            sx={{
                                minWidth: '213px',
                                minHeight: '70px',
                                backgroundColor: common.white,
                                borderRadius: '50px',
                                display: 'flex',
                                justifyContent: 'start',
                                gap: '10px',
                                padding: 0,
                                textTransform: 'none',
                                color: common.black,
                            }}
                            id="fade-button"
                            aria-controls={open ? 'fade-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleClick}
                            endIcon={<ExpandMoreIcon />}
                        >
                            <Avatar
                                sx={{
                                    minWidth: '70px',
                                    height: '70px',
                                }}
                                alt="Avatar"
                                src={userInfo.attributes?.avatar ?? ''}
                            />

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'start',
                                    textAlign: 'start',
                                    gap: '5px',
                                }}
                            >
                                <Box
                                    sx={{
                                        fontSize: '20px',
                                        lineHeight: '20px',
                                        fontWeight: '600',
                                        maxWidth: '80px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {userInfo.attributes?.username}
                                </Box>
                                <Box
                                    sx={{
                                        fontSize: '16px',
                                        lineHeight: '20px',
                                        fontWeight: '300',
                                    }}
                                >
                                    Cơ bản
                                </Box>
                            </Box>
                        </Button>
                        <Menu
                            id="fade-menu"
                            MenuListProps={{
                                'aria-labelledby': 'fade-button',
                            }}
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            TransitionComponent={Fade}
                            sx={styles.profileDropdown}
                        >
                            <MenuItem
                                disableRipple
                                onClick={() => navigate('/admin/profile')}
                            >
                                {t('common.user.infomation')}
                            </MenuItem>
                            <MenuItem
                                disableRipple
                                onClick={() =>
                                    navigate('/admin/change-password')
                                }
                            >
                                {t('common.change.password')}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>
            </FormProvider>
            {isFilterOpen('campaignFilter') && (
                <MainHeaderCampaignFilterPopover
                    headerFilterId="campaignFilterMenu"
                    anchorEl={filterState['campaignFilter']?.anchorEl}
                    open={isFilterOpen('campaignFilter')}
                    handleClose={() => handleFilterClose('campaignFilter')}
                />
            )}
            {isFilterOpen('paymentFilter') && (
                <PaymentFilter
                    headerFilterId="paymentFilterMenu"
                    anchorEl={filterState['paymentFilter']?.anchorEl}
                    open={isFilterOpen('paymentFilter')}
                    handleClose={() => handleFilterClose('paymentFilter')}
                />
            )}
            {userInfo.attributes.role !== 'client' &&
                isFilterOpen('accountFilter') && (
                    <AccountFilter
                        headerFilterId="accountFilterMenu"
                        anchorEl={filterState['accountFilter']?.anchorEl}
                        open={isFilterOpen('accountFilter')}
                        handleClose={() => handleFilterClose('accountFilter')}
                    />
                )}
            {userInfo.attributes.role !== 'client' &&
                isFilterOpen('customerFilter') && (
                    <CustomerFilter
                        headerFilterId="customerFilterMenu"
                        anchorEl={filterState['customerFilter']?.anchorEl}
                        open={isFilterOpen('customerFilter')}
                        handleClose={() => handleFilterClose('customerFilter')}
                    />
                )}
            {isFilterOpen('overviewFilter') && (
                <OverviewFilter
                    headerFilterId="overviewFilter"
                    anchorEl={filterState['overviewFilter']?.anchorEl}
                    open={isFilterOpen('overviewFilter')}
                    handleClose={() => handleFilterClose('overviewFilter')}
                />
            )}
            {isFilterOpen('campaignOverviewFilter') && (
                <CampaignOverviewFilter
                    headerFilterId="campaignOverviewFilter"
                    anchorEl={filterState['campaignOverviewFilter']?.anchorEl}
                    open={isFilterOpen('campaignOverviewFilter')}
                    handleClose={() =>
                        handleFilterClose('campaignOverviewFilter')
                    }
                />
            )}
            {userInfo.attributes.role !== 'client' &&
                isFilterOpen('dataCollectFilter') && (
                    <DataCollectFilter
                        headerFilterId="dataCollectFilter"
                        anchorEl={filterState['dataCollectFilter']?.anchorEl}
                        open={isFilterOpen('dataCollectFilter')}
                        handleClose={() =>
                            handleFilterClose('dataCollectFilter')
                        }
                    />
                )}
            {addCampaignDialogOpen && (
                <CampaignForm
                    open={addCampaignDialogOpen}
                    onClose={() => setAddCampaignDialogOpen(false)}
                    actionType="add"
                />
            )}
            {idPopover && (
                <Notification
                    idPopover={idPopover}
                    anchorElNotification={anchorElNotification}
                    openPopover={openPopover}
                    handleCloseNotification={handleCloseNotification}
                />
            )}
        </Box>
    );
};
const styles = createStyleSheet({
    search: {
        flex: 1,
        '& .MuiInputBase-root': {
            ...styleInput['& .MuiInputBase-root'],
            height: '70px',
            fontSize: '22px',
            '& fieldset': {
                border: 'none',
            },
        },
    },
    btnAddCampaign: {
        minWidth: '317px',
        height: '70px',
        fontSize: '22px',
        fontWeight: '600',
        textTransform: 'none',
        borderRadius: '50px',
        backgroundColor: common.white,
        color: common.black,
        boxShadow: 'none',
        border: 'none',
        '&:hover': {
            backgroundColor: common.white,
        },
    },
    profileDropdown: {
        '& .MuiMenu-paper': {
            borderRadius: '10px',
            width: '213px',
            marginTop: '9px',
            minHeight: '98px',
            fontSize: '18px',
            padding: '21px 48px 20px 16px',
            '& .MuiMenu-list ': {
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '13px',
                '& .MuiMenuItem-root': {
                    padding: 0,
                    '&:hover': {
                        backgroundColor: 'unset',
                    },
                },
            },
        },
    },
});
export default MainHeader;
